import { CheckCircle, DragIndicator, ExpandMore, Refresh } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React from 'react';

interface AdjustGroupingsProps {
  adjustedWeightClasses: Array<{ name: string; wrestlers: any[]; minWeight: number; maxWeight: number; format?: string; rounds?: any[] }>;
  dragOverClass: string;
  onDragStart: (e: React.DragEvent, wrestler: any, fromClass: string) => void;
  onDragOver: (e: React.DragEvent, className: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, className: string) => void;
  onGroupFormatChange: (className: string, format: string) => void;
  onRefreshGroup: () => void;
  onBackClick: () => void;
  onContinueClick: () => void;
  groupFormats: Array<{ value: string; label: string }>;
  generateMatches: (wrestlers: any[]) => any[];
  flattenRoundsToMatches: (weightClass: any) => any[];
}

const AdjustGroupings: React.FC<AdjustGroupingsProps> = ({
  adjustedWeightClasses,
  dragOverClass,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onGroupFormatChange,
  onRefreshGroup,
  onBackClick,
  onContinueClick,
  groupFormats,
  generateMatches,
  flattenRoundsToMatches
}) => {
  return (
    <>
          <Box>
            <Typography variant="h6" gutterBottom>
              Step 2: Adjust Groupings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Drag and drop wrestlers between weight classes to balance competition. Move skilled wrestlers up or less experienced wrestlers down.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <DragIndicator sx={{ mr: 1 }} />
              Drag wrestlers between groups to adjust weight classes. This is common for less serious tournaments to balance competition.
            </Alert>

            <Grid container spacing={3}>
              {adjustedWeightClasses.map((weightClass) => (
                <Grid item xs={12} sm={6} md={4} key={weightClass.name}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      border: dragOverClass === weightClass.name ? '2px dashed' : '1px solid',
                      borderColor: dragOverClass === weightClass.name ? 'primary.main' : 'grey.300',
                      backgroundColor: dragOverClass === weightClass.name ? 'action.hover' : 'background.paper',
                    }}
                    onDragOver={(e) => onDragOver(e, weightClass.name)}
                    onDragLeave={onDragLeave}
                    onDrop={(e) => onDrop(e, weightClass.name)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle1" gutterBottom>
                          {weightClass.name}
                        </Typography>
                        <CheckCircle></CheckCircle>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {weightClass.wrestlers.length} wrestlers ({weightClass.minWeight.toFixed(1)}-{weightClass.maxWeight.toFixed(1)} kg)
                        </Typography>
                      </Box>
                      <Box sx={{ minHeight: 120, maxHeight: 200, overflow: 'auto' }}>
                        {weightClass.wrestlers.map((wrestler, index) => (
                          <Chip
                            key={`${wrestler.firstName}-${wrestler.lastName}-${wrestler.schoolName}`}
                            label={`${wrestler.firstName} ${wrestler.lastName} (${wrestler.weightKg.toFixed(1)}kg)`}
                            size="small"
                            variant="outlined"
                            draggable
                            onDragStart={(e) => onDragStart(e, wrestler, weightClass.name)}
                            sx={{ 
                              mr: 0.5, 
                              mb: 0.5,
                              cursor: 'grab',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                cursor: 'grabbing'
                              }
                            }}
                          />
                        ))}
                      </Box>
 
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                        
                        <Accordion>
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                          >
                          <Typography>Setup:</Typography>
                          </AccordionSummary>
                          <AccordionDetails>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          
                          <InputLabel>Format</InputLabel>
                          <Select
                            value={weightClass.format || 'round-robin'}
                            label="Format"
                            onChange={(e: any) => onGroupFormatChange(weightClass.name, e.target.value)}
                          >
                            {groupFormats.map((format) => (
                              <MenuItem key={format.value} value={format.value}>
                                {format.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Refresh />}
                            onClick={onRefreshGroup}
                          >
                          </Button>

                        </FormControl>
                      </Box>
                       <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
                          Required Matches ({generateMatches(weightClass.wrestlers).length})
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          {weightClass.wrestlers.length === 2 && "Best of 3 format"}
                          {weightClass.wrestlers.length === 3 && "Round robin format"}
                          {weightClass.wrestlers.length >= 4 && "Single elimination format"}
                        </Typography>
                        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                          {weightClass.rounds ? (
                            // Display round-based matches if rounds exist
                            weightClass.rounds.map((round: any) => (
                              <Box key={round.roundNumber} sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1 }}>
                                  Round {round.roundNumber}
                                </Typography>
                                {round.matches.map((match: any, matchIndex: number) => (
                                  <Box key={matchIndex} sx={{ 
                                    mb: 1, 
                                    p: 1, 
                                    backgroundColor: 'grey.50', 
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        Round {round.roundNumber}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Typography variant="caption" sx={{ display: 'inline' }}>
                                          {match.wrestler1.firstName} {match.wrestler1.lastName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ mx: 1, display: 'inline' }}>
                                          vs
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'inline' }}>
                                          {match.wrestler2.firstName} {match.wrestler2.lastName}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                ))}
                                {round.byes.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {round.byes.map((byeWrestler: any) => 
                                      `${byeWrestler.firstName} ${byeWrestler.lastName}`
                                    ).join(', ')} is off
                                  </Typography>
                                )}
                              </Box>
                            ))
                          ) : (
                            // Fallback to old flat match display
                            flattenRoundsToMatches(weightClass).map((match, index) => (
                              <Box key={index} sx={{ 
                                mb: 1, 
                                p: 1, 
                                backgroundColor: 'grey.50', 
                                borderRadius: 1,
                                fontSize: '0.75rem'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                    Round {match.roundNumber}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="caption" sx={{ display: 'inline' }}>
                                      {match.wrestler1.firstName} {match.wrestler1.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mx: 1, display: 'inline' }}>
                                      vs
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'inline' }}>
                                      {match.wrestler2.firstName} {match.wrestler2.lastName}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                            ))
                          )}
                        </Box>
                          </AccordionDetails>
                        </Accordion>
                        
 
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={onBackClick}>
                Back
              </Button>
              <Button variant="contained" onClick={onContinueClick}>
                Continue to Generate Draw
              </Button>
            </Box>
          </Box>
    </>
  )
}

export default AdjustGroupings;
